import { isWorkspaceLink, workspaceKind, type WorkspaceKind } from "./workspace-links.ts";
import { parseAccountUrl, type PlatformKey } from "./platforms.ts";

/**
 * What a note is made of.
 *
 * A note is plain text with two things in it, and only two, because every extra
 * syntax is another thing somebody has to know before they can write one:
 *
 *  - `@username`, a person
 *  - `[label](url)`, a link
 *
 * Every inline button is the second one. The icon is not part of the syntax and
 * is never stored: it is derived from the URL when the note is read, by the same
 * two functions that decide the icons on a creator's rails. That is what makes a
 * pasted link and one inserted from the `#` menu render identically, and it
 * means a link cannot end up wearing the wrong badge because somebody picked it
 * from the wrong menu.
 *
 * A bare URL typed into the middle of a sentence is treated as `[url](url)`,
 * because somebody pasting a link into a note means the link, and making them
 * wrap it in brackets first would be a syntax lesson nobody asked for.
 *
 * Free of any Node or React import: the composer, the renderer and the server
 * all need it, and the server needs it to know who was mentioned.
 */

export type NoteToken =
  | { kind: "text"; text: string }
  | { kind: "mention"; handle: string }
  | { kind: "link"; label: string; url: string; icon: LinkIcon; tracking: boolean };

/** What to draw beside a link, decided by the link itself. */
export type LinkIcon =
  | { kind: "workspace"; workspace: WorkspaceKind }
  | { kind: "platform"; platform: PlatformKey }
  | { kind: "web" };

/**
 * A username, and the characters one is allowed to contain.
 *
 * Deliberately narrow. An email address is a valid way to address somebody in
 * the composer, but what lands in the note is the username, so the stored text
 * has one shape and a person renamed later can be found by one lookup.
 */
const MENTION = /@([A-Za-z0-9][A-Za-z0-9._-]*)/y;
const MARKDOWN = /\[([^\]\n]{1,120})\]\((https?:\/\/[^\s)]{1,900})\)/y;
const BARE_URL = /https?:\/\/[^\s<>"')\]]{4,900}/y;

/**
 * A link that must never be opened.
 *
 * OnlyFans trial and tracking links are live: loading one registers a click
 * against the creator whose earnings are on the same page, so somebody checking
 * where a link goes quietly pollutes the number they came to read. Written into
 * a note they look like any other link, which is exactly the problem.
 *
 * Two shapes, both of them a path under a username:
 *
 *   /{user}/trial/{token}   a free trial
 *   /{user}/c{number}       a tracking link
 */
export function isTrackingLink(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (!/(^|\.)onlyfans\.com$/i.test(parsed.hostname)) return false;
    return /^\/[^/]+\/(trial\/[A-Za-z0-9_-]{4,}|c\d+)$/i.test(parsed.pathname.replace(/\/+$/, ""));
  } catch {
    return false;
  }
}

export function iconFor(url: string): LinkIcon {
  if (isWorkspaceLink(url)) return { kind: "workspace", workspace: workspaceKind(url) };

  const account = parseAccountUrl(url);
  // `other` is the parser's way of saying it recognised a URL but not a
  // platform, which is a globe rather than a badge for a site nobody named.
  if (account && account.platform !== "other") return { kind: "platform", platform: account.platform };

  return { kind: "web" };
}

/** A URL shown as itself, with the parts nobody reads taken off. */
export function tidyUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, "");
    return `${parsed.hostname.replace(/^www\./, "")}${path}`.slice(0, 60);
  } catch {
    return url.slice(0, 60);
  }
}

/**
 * Split a note into what to draw.
 *
 * One pass with sticky regexes rather than a chain of `String#replace`, so a URL
 * inside a markdown label cannot be rewritten out from under the link that
 * contains it.
 */
export function parseNote(body: string): NoteToken[] {
  const tokens: NoteToken[] = [];
  let text = "";
  let index = 0;

  const flush = () => {
    if (text) tokens.push({ kind: "text", text });
    text = "";
  };

  while (index < body.length) {
    const char = body[index];

    if (char === "[") {
      MARKDOWN.lastIndex = index;
      const match = MARKDOWN.exec(body);
      if (match) {
        flush();
        tokens.push({ kind: "link", label: match[1], url: match[2], icon: iconFor(match[2]), tracking: isTrackingLink(match[2]) });
        index = MARKDOWN.lastIndex;
        continue;
      }
    }

    if (char === "@") {
      // Only at the start of a word. Otherwise "pat@example.com" addresses a
      // person called example.com, and the note quietly pings nobody while
      // looking like it pinged somebody.
      const before = index === 0 ? "" : body[index - 1];
      MENTION.lastIndex = index;
      const match = /[\s(]/.test(before) || before === "" ? MENTION.exec(body) : null;
      if (match) {
        flush();
        tokens.push({ kind: "mention", handle: match[1] });
        index = MENTION.lastIndex;
        continue;
      }
    }

    if (char === "h" && (body.startsWith("http://", index) || body.startsWith("https://", index))) {
      BARE_URL.lastIndex = index;
      const match = BARE_URL.exec(body);
      if (match) {
        // Trailing punctuation belongs to the sentence, not to the link.
        const url = match[0].replace(/[.,;:!?]+$/, "");
        flush();
        tokens.push({ kind: "link", label: tidyUrl(url), url, icon: iconFor(url), tracking: isTrackingLink(url) });
        index += url.length;
        continue;
      }
    }

    text += char;
    index += 1;
  }

  flush();
  return tokens;
}

/** Who a note addresses, lower-cased and deduplicated, in the order written. */
export function mentionsIn(body: string): string[] {
  const seen = new Set<string>();
  for (const token of parseNote(body)) {
    if (token.kind === "mention") seen.add(token.handle.toLowerCase());
  }
  return [...seen];
}

/** Every link in a note, for the access check that decides how each one renders. */
export function linksIn(body: string): string[] {
  const seen = new Set<string>();
  for (const token of parseNote(body)) {
    if (token.kind === "link") seen.add(token.url);
  }
  return [...seen];
}

// ── the composer's shortcuts ─────────────────────────────────────────────

/**
 * `@#ig` and the rest.
 *
 * Typing the whole of `https://instagram.com/` before a username is the sort of
 * thing that stops people writing notes at all, so the platforms worth naming
 * get two or three letters. What the shortcut produces is an ordinary
 * `[label](url)`: the shortcut is a way of typing, never a thing that ends up
 * stored, so a note written by somebody who does not know the shortcuts exist
 * reads exactly the same.
 */
export const SOCIAL_SHORTCUTS: Record<string, PlatformKey> = {
  insta: "instagram",
  ig: "instagram",
  r: "reddit",
  tw: "twitter",
  x: "twitter",
  tt: "tiktok",
  fb: "facebook",
  of: "onlyfans",
  yt: "youtube",
  ph: "pornhub",
  xv: "xvideos",
  xh: "xhamster",
  cb: "chaturbate",
};

/**
 * What `@#` offers, in the order it offers them.
 *
 * The first six are what people actually write about, in the order they were
 * asked for. Everything after is alphabetical and rarely reached, because by
 * then somebody has typed enough letters to be filtering rather than browsing.
 */
export const SOCIAL_ORDER: PlatformKey[] = [
  "instagram",
  "reddit",
  "twitter",
  "tiktok",
  "facebook",
  "onlyfans",
  "youtube",
  "chaturbate",
  "pornhub",
  "xhamster",
  "xvideos",
];

/**
 * The profile URL a handle means on a platform.
 *
 * `twitter.com` rather than `x.com`, and `tiktok.com/@` with the at sign, because
 * these are the forms the team writes and reads. The handle is escaped, so a
 * space or a slash typed by accident cannot change which page the link goes to.
 */
const PROFILE_BASE: Partial<Record<PlatformKey, string>> = {
  instagram: "https://instagram.com/",
  reddit: "https://reddit.com/u/",
  twitter: "https://twitter.com/",
  tiktok: "https://tiktok.com/@",
  facebook: "https://facebook.com/",
  onlyfans: "https://onlyfans.com/",
  youtube: "https://youtube.com/@",
  pornhub: "https://pornhub.com/model/",
  xvideos: "https://xvideos.com/profiles/",
  xhamster: "https://xhamster.com/users/",
  chaturbate: "https://chaturbate.com/",
};

export function profileUrl(platform: PlatformKey, handle: string): string {
  const name = handle.trim().replace(/^@+/, "").replace(/^u\//, "");
  return `${PROFILE_BASE[platform] ?? "https://"}${encodeURIComponent(name)}`;
}

/** The markdown a chosen link becomes. */
export function linkToken(label: string, url: string): string {
  // A label carrying a bracket would end the link early and leave the rest of
  // it as prose, so those two characters are the only ones touched.
  return `[${label.replace(/[[\]]/g, "").trim() || tidyUrl(url)}](${url})`;
}

/**
 * What is being typed at the caret, so the composer knows which menu to offer.
 *
 * Returns the trigger and the letters after it. Only ever looks at the current
 * word, so a `#` in the middle of a sentence written earlier cannot reopen a
 * menu when somebody edits the end of the line.
 */
export type Trigger =
  | { kind: "mention"; query: string; from: number }
  | { kind: "link"; query: string; from: number }
  /** `@#` with no platform decided yet: the menu is the list of platforms. */
  | { kind: "platform"; query: string; from: number }
  | { kind: "social"; platform: PlatformKey; query: string; from: number }
  | null;

export function triggerAt(body: string, caret: number): Trigger {
  const upto = body.slice(0, caret);

  /*
   * `@#` first: it starts with @ and would otherwise read as a mention.
   *
   * Two states, and the difference is whether a platform has been settled. Bare
   * `@#`, or letters that are not yet a shortcut, offer the list. A shortcut
   * followed by a space or a colon has settled it, and what comes after is the
   * handle.
   */
  const chosen = /@#([A-Za-z]+)[ :]([^\s]*)$/.exec(upto);
  if (chosen) {
    const platform = SOCIAL_SHORTCUTS[chosen[1].toLowerCase()];
    if (platform) return { kind: "social", platform, query: chosen[2], from: caret - chosen[0].length };
  }

  const picking = /@#([A-Za-z]*)$/.exec(upto);
  if (picking) {
    const exact = SOCIAL_SHORTCUTS[picking[1].toLowerCase()];
    // An exact shortcut with nothing after it is still a choice being made:
    // `@#i` could become `@#ig`, so the list stays up until a space settles it.
    return exact && picking[1].length > 1
      ? { kind: "social", platform: exact, query: "", from: caret - picking[0].length }
      : { kind: "platform", query: picking[1], from: caret - picking[0].length };
  }

  const mention = /(?:^|\s)@([A-Za-z0-9._@-]*)$/.exec(upto);
  if (mention) return { kind: "mention", query: mention[1], from: caret - mention[1].length - 1 };

  /*
   * Link names have spaces in them ("INFLOWW LINKS"), so the query cannot stop
   * at the first one. It stops at two, which covers every name in the registry
   * and means a `#` written earlier in a sentence stops offering a menu after a
   * few words rather than following the caret to the end of the line.
   */
  const link = /(?:^|\s)#([^\n#]{0,28})$/.exec(upto);
  if (link && (link[1].match(/ /g)?.length ?? 0) <= 2) {
    return { kind: "link", query: link[1], from: caret - link[1].length - 1 };
  }

  return null;
}
